import { Modal, Form, Button, Container, Row, Col, Offcanvas, Toast, ToastContainer } from "react-bootstrap"
import { EntityDetailsDisplayInfo, EntityDetailsDisplayInfoField, EntityDetailsDisplayInfoFieldPassengersLeg, EntityDetailsDisplayInfoFieldRoute, EntityDetailsDisplayInfoSection } from "./EntityDetailsDisplayInfo"
import { useEffect, useMemo, useState } from "react"
import EntityInputBox from "./EntityInputBox"
import { Identifiable, implementsCanEditSharedValues } from "../../models/identifiable"
import "../../layouts/sidemenu/SideMenuLayout.css"
import "../../css/bootstrap-custom-sizes.css"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBoxArchive, faCaretRight, faTrash } from '@fortawesome/free-solid-svg-icons';
import EntityDatePicker from "./EntityDatePicker"
import EntityIntermediateStops from "./EntityIntermediateStops"
import EntityAircraftPicker from "./flight/EntityAircraftPicker"
import EntityRoutePicker from "./flight/EntityRoutePicker"
import EntityReverseRoutePicker from "./flight/EntityReverseRoutePicker"
import EntityCrewPicker from "./flight/EntityCrewPicker"
import EntityDropdown from "./flight/EntityDropdown"
import EntityDropdownWithInput from "./flight/EntityDropdownWithInput"
import EntityPassengersComponent from "./flight/EntityPassengersComponent"
import EntityTextAreaBox from "./EntityTextAreaBox"
import EntityDateTimePicker from "./EntityDateTimePicker"
import EntityAircraftSarComponent from "./flight/EntityAircraftSarComponent"
import EntityBooleanToggle from "./EntityBooleanToggle"
import { Control, FieldValues, Path, useForm, UseFormResetField, UseFormSetFocus, UseFormSetValue } from "react-hook-form";
import Flight, { FlightFplFile, isIfrFlightRules } from "../../models/flight"
import { getFplCoordsFromRoutePoint } from "../../models/route"
import EntityAftnAddressesComponent from "./flight/EntityAftnAddressesComponent"
import EntityFlightIdComponent from "./flight/EntityFlightIdComponent"
import { Outlet, useNavigate, useOutletContext } from "react-router-dom"
import EntityFlightWbComponent from "./flight/EntityFlightWbComponent"
import EntityFuelOnBoardComponent from "./flight/EntityFuelOnBoardComponent"
import { discardBriefing } from "../../store/flightSlice"
import { useAppDispatch } from "../../store/hooks"
import EntityPobComponent from "./flight/EntityPobComponent"
import EntityTimeWithAutoSetComponent from "./flight/EntityTimeWithAutoSetComponent"
import moment from "moment"
import { ListFlightsContext } from "../../pages/organization/flights/ListFlighsPage"
import EntityFlightPlanPointPicker from "./flight/EntityFlightPlanPointPicker"
import EntityCustomRouteComponent from "./flight/EntityCustomRouteComponent"
import FlightPlan, { FlightPlanPoint, getRoutePointFromFlightPlanPoint } from "../../models/flightPlan"
import EntityPassengersLegComponent, { EntityPassengersLegComponentProps } from "./flight/EntityPassengersLegComponent"
import EntitySignatureComponent from "./flight/EntitySignatureComponent"
import ToolsFlight from "../../models/toolsFlight"
import EntityAircraftWbProfilePicker from "./flight/EntityAircraftWbProfilePicker"
import EntityFlightAirfieldSlotComponent from "./flight/EntityFlightAirfieldSlotComponent"
import EntityIfrRoutePlanner from "./flight/EntityIfrRoutePlanner"

interface EntityDetailsPageProps<Entity extends FieldValues> {
  entity: Entity;
  handleClose: () => void;
  displayInfo: EntityDetailsDisplayInfo<Entity>;
  dispatchSaveEntity: (entity: Entity) => Promise<Entity>;
  dispatchDeleteEntity?: (entity: Entity) => Promise<void>
  extraHeaderContent?: JSX.Element
}
  
  
function EntityDetailsPage<Entity extends Identifiable>({entity, handleClose, displayInfo, dispatchSaveEntity, dispatchDeleteEntity, extraHeaderContent}: EntityDetailsPageProps<Entity>){
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const outletContext = useOutletContext()
  
  //A flight ON a saved library route persists only `route`; its DEP/DEST fields render from a
  //`customRoute` mirror that is display-only and deliberately NOT stored (stripped in avitracerApi's
  //withoutDerivedCustomRoute, because the API forbids route + customRoute together). Rebuild that
  //mirror on load so the locked aerodrome fields show for a saved-route IFR flight, exactly as they
  //do at the moment the route is picked. Done via useForm `values` (not setValue) so it does not
  //emit a named change event and therefore cannot trip the stale-route watcher below.
  const formEntity = useMemo(() => {
    if (displayInfo.entityName !== "Flight") return entity
    const f = entity as unknown as Flight
    if (!f.route || f.customRoute || !f.flightRules || !isIfrFlightRules(f.flightRules)) return entity
    return {
      ...f,
      customRoute: {
        departure: { isIcao: true, icaoCode: f.route.departure?.name ?? "", coords: getFplCoordsFromRoutePoint(f.route.departure) },
        destination: { isIcao: true, icaoCode: f.route.destination?.name ?? "", coords: getFplCoordsFromRoutePoint(f.route.destination) },
        waypoints: [],
        intermediateStops: [],
      },
    } as unknown as Entity
  }, [entity, displayInfo.entityName])

  const { control, handleSubmit, setValue, watch, formState:{isSubmitting, dirtyFields}, resetField, setFocus, trigger } = useForm<Entity>({values: formEntity});

  const [lockedFlightReason, setLockedFlightReason] = useState<"fplFile" | "briefing" | "scheduled" | undefined>(undefined);
  const [showDiscardFlightOffCanvas, setShowDiscardFlightOffCanvas] = useState(false);
  const [isDiscardingFlightBriefing, setIsDiscardingFlightBriefing] = useState(false);

  const edittedEntity = watch();

  const [activeInfoSection, setActiveInfoSection] = useState(displayInfo.sections[0])
  //Open/closed state for sections declared collapsedByDefault, keyed by sectionId.
  const [expandedSections, setExpandedSections] = useState<{ [sectionId: string]: boolean }>({})

  const [modalContent, setModalContent] = useState<JSX.Element | null>(null)
  const [showCloseInModalContent, setShowCloseInModalContent] = useState<boolean>(true)
  const [sideAlertContent, setSideAlertContent] = useState<JSX.Element | null>(null)

  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  const handleSubmitAfterValidation = async (data: any) => {
    var isPureIfr = false
    if (displayInfo.entityName === "Flight" || displayInfo.entityName === "Flight Plan"){
      const fpl = data as Flight | FlightPlan
      isPureIfr = fpl.flightRules === "I"
      //Z (VFR first) legitimately files a VFR initial level — the IFR level lives inside the route.
      if (fpl.flightRules !== "V" && fpl.flightRules !== "Z" && fpl.cruiseLevel?.unit === "VFR"){
        setErrorMessage("IFR flights require a cruising flight level (e.g. FL080), not VFR")
        return
      }
    }

    //Pure IFR (I) is addressed to the IFPS units by the backend — no AFTN addresses needed. VFR and
    //mixed-rules (Y/Z) plans need them: IFPS does not distribute the VFR segments.
    if (displayInfo.entityName === "Flight Plan" && !isPureIfr){
      if (!data.aftnAddresses || (data.aftnAddresses as string[]).filter((s) => s.length > 0).length === 0){
        setErrorMessage("AFTN addresses are required")
        setFocus("aftnAddresses" as Path<Entity>)
        return
      }
    }else if (displayInfo.entityName === "Flight" && !isPureIfr && (data as Flight).customRoute){
      const aftns = (data as Flight).customRoute?.aftnAddresses
      if (!aftns || (aftns as string[]).filter((s) => s.length > 0).length === 0){
        setErrorMessage("AFTN addresses are required")
        setFocus("customRoute.aftnAddresses" as Path<Entity>)
        return
      }
    }

    var savedEntityId: string
    try {
      const savedEntity = await dispatchSaveEntity(data);
      savedEntityId = savedEntity.id!
    }catch(e: any){
      setErrorMessage(e.message);
      return;
    }
    
    if (displayInfo.entityName === "Flight" || displayInfo.entityName === "Flight Plan"){
      const flight = data as Flight | FlightPlan
      const {setSelectedTab} = outletContext as ListFlightsContext
      if (moment(flight.departureDateTime).utc().isSame(moment().utc(),"day")){
        setSelectedTab("today")
      }else if (moment(flight.departureDateTime).utc().isSame(moment().utc().add(1,"day"),"day")){
        setSelectedTab("tomorrow")
      }else if (moment(flight.departureDateTime).utc().isAfter(moment().utc().add(1,"day"),"day")){
        setSelectedTab("upcoming")
      }else{
        setSelectedTab("archived")
      }
    }
    
    if (displayInfo.entityName === "Flight Plan"){
      navigate(`/fpls/${savedEntityId}/fpl`)
    }else if (displayInfo.entityName !== "Quick Flight Plan"){
      handleClose();
    }
  };

  const handleDelete = async () => {
    try {
      if (edittedEntity.id) {
        await dispatchDeleteEntity!(edittedEntity);
      }
    }catch(e: any){
      setErrorMessage(e.message);
      return;
    }

    handleClose();
  }; 

  //Derive the effective lock per render — never write it back into displayInfo. The parent builds that
  //object once, so it is the SAME object across this child's re-renders: `i.disabled = i.disabled || ...`
  //ORed a true into the definition itself and latched it for the life of the mount. The data was always
  //right; the field simply never unlocked until a reload (e.g. after "Use as custom route").
  const shouldDisplaySections = displayInfo.sections
    .filter((s) => s.shouldDisplay ? s.shouldDisplay(edittedEntity) : true)
    .map((s) => {
      const lockedSection = implementsCanEditSharedValues(entity)
        ? !entity.canEditSharedValues && (s.containsSharedValues ?? false)
        : false
      return {
        ...s,
        inputs: s.inputs.map((i) => ({
          ...i,
          disabled: (i.disabled ?? false) || lockedFlightReason !== undefined || lockedSection || (i.disabledWhen?.(edittedEntity) ?? false)
        }))
      }
    })

  const handleClickActiveSection = (newSection: EntityDetailsDisplayInfoSection<Entity>) => {
    document.getElementById("scrollableFormRow")!.scrollTo({ top: document.getElementById(newSection.sectionId)!.offsetTop - 40, behavior: "smooth" });
  }

  const handleScrollInFormRow = () => {
    const scrollableFormRow = document.getElementById("scrollableFormRow") as HTMLElement
    const sections = document.querySelectorAll(".scrollableFormRowSection") as NodeListOf<HTMLElement>;
    sections.forEach(section => {
      if (scrollableFormRow.scrollTop >= section.offsetTop - 41) {
        setActiveInfoSection(displayInfo.sections.find((s) => s.sectionId === section.id)!);
      }
    });

  }

  useEffect(() => {
    document.getElementById("scrollableFormRow")?.addEventListener("scroll", handleScrollInFormRow)
    return () => document.getElementById("scrollableFormRow")?.removeEventListener("scroll", handleScrollInFormRow)
  })

  // For IFR plans, a previously validated route (ifpsRoute) becomes stale when the departure,
  // destination, route or cruise level change. Clear it so the user must re-find an IFR route.
  useEffect(() => {
    if (displayInfo.entityName !== "Flight" && displayInfo.entityName !== "Flight Plan") return;
    const subscription = watch((value, info) => {
      const name = info.name as string | undefined;
      if (!name) return;
      const clearsRoute =
        name === "departure" || name.startsWith("departure.") ||
        name === "destination" || name.startsWith("destination.") ||
        name === "route" ||
        name.startsWith("customRoute.departure") || name.startsWith("customRoute.destination") ||
        name === "cruiseLevel" || name.startsWith("cruiseLevel.");
      if (clearsRoute && (value as any).ifpsRoute) {
        setValue("ifpsRoute" as Path<Entity>, "" as any, { shouldDirty: true });
      }
      // Rules change adjusts the cruise level to what item 15a must carry: I/Y start IFR, so a VFR
      // level defaults to a usable FL080; Z (VFR first) files "VFR" as its initial level, so
      // switching (back) to Z sets the level to VFR automatically.
      if (name === "flightRules") {
        const v = value as any;
        if ((v.flightRules === "I" || v.flightRules === "Y") && v.cruiseLevel?.unit === "VFR") {
          setValue("cruiseLevel" as Path<Entity>, { unit: "F", value: 80 } as any, { shouldDirty: true });
        }
        if (v.flightRules === "Z" && v.cruiseLevel?.unit !== "VFR") {
          //value "VFR" matches the dropdown's hardcoded VFR value, avoiding a second normalization write
          setValue("cruiseLevel" as Path<Entity>, { unit: "VFR", value: "VFR" } as any, { shouldDirty: true });
        }
      }
      // Z files a VFR initial level in item 15a — lock it: any attempt to change the cruise level away
      // from VFR on a Z flight snaps back. (The IFR-portion level lives in the route's pick-up group.)
      if ((name === "cruiseLevel" || name.startsWith("cruiseLevel.")) &&
          (value as any).flightRules === "Z" && (value as any).cruiseLevel?.unit !== "VFR") {
        setValue("cruiseLevel" as Path<Entity>, { unit: "VFR", value: "VFR" } as any, { shouldDirty: true });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue, displayInfo.entityName])

  function getSpecificInfoField(infoField: EntityDetailsDisplayInfoField<Entity>) {
    if (infoField.type === "text") {
      if (infoField.multiline) {
        return (
          <EntityTextAreaBox
            displayInfoInput={infoField}
            control={control}
          />
        );
      } else {
        return (
          <EntityInputBox displayInfoInput={infoField} control={control} />
        );
      }
    } else if (infoField.type === "number") {
      return (
        <EntityInputBox
          displayInfoInput={infoField}
          control={control}
        />
      );
    } else if (infoField.type === "time-autoset") {
      return (
        <EntityTimeWithAutoSetComponent
          displayInfoInput={infoField}
          control={control}
          edittedEntity={edittedEntity}
        />
      );
      
    } else if (infoField.type === "boolean") {
      return (
        <EntityBooleanToggle
          displayInfoInput={infoField}
          control={control}
        />
      );
    } else if (infoField.type === "date") {
      return (
        <EntityDatePicker
          displayInfoInput={infoField}
          control={control}
        />
      );
    } else if (infoField.type === "dateTime") {
      return (
        <EntityDateTimePicker
          displayInfoInput={infoField}
          control={control}
        />
      );
    }else if (infoField.type === "intermediateStop") {
      return (
        <EntityIntermediateStops
          edittedEntity={edittedEntity}
          setSideAlertContent={setSideAlertContent}
          displayInfoInput={infoField}
          control={control}
        />
      );
    } else if (infoField.type === "aircraft") {
      return (
        <EntityAircraftPicker
          displayInfoInput={infoField}
          setModalContent={setModalContent}
          control={control}
          setValue={setValue as unknown as UseFormSetValue<Flight>}
        />
      );
    } else if (infoField.type === "aircraftWbProfileName") {
      return (
        <EntityAircraftWbProfilePicker
          displayInfoInput={infoField}
          setModalContent={setModalContent}
          control={control}
        />
      );
    } else if (infoField.type === "aircraftSar") {
      return (
        <EntityAircraftSarComponent
          displayInfoInput={infoField}
          control={control}
          setModalContent={setModalContent}
        />
      );
    } else if (infoField.type === "route") {
      return (
        <EntityRoutePicker
          displayInfoInput={infoField as unknown as EntityDetailsDisplayInfoFieldRoute<Flight>}
          setModalContent={setModalContent}
          control={control as unknown as Control<Flight, any>}
          setValue={setValue as unknown as UseFormSetValue<Flight>}
        />
      );
    } else if (infoField.type === "reverseRoute") {
      return (
        <EntityReverseRoutePicker
          displayInfoInput={infoField}
          setModalContent={setModalContent}
          control={control}
          setValue={setValue}
        />
      );
    } else if (infoField.type === "passengersLeg") {
      var routePart: EntityPassengersLegComponentProps<Entity>["route"]
      const flight: Flight = edittedEntity as unknown as Flight
      if (flight.customRoute){
        routePart = {
          departure: getRoutePointFromFlightPlanPoint(flight.customRoute.departure),
          destination: getRoutePointFromFlightPlanPoint(flight.customRoute.destination),
          intermediateStops: flight.customRoute.intermediateStops ?? []
        }
      }else{
        routePart = flight.route!
      }
      return (
        <EntityPassengersLegComponent
          displayInfoInput={infoField  as unknown as EntityDetailsDisplayInfoFieldPassengersLeg<Flight>}
          control={control as unknown as Control<Flight, any>}
          setModalContent={setModalContent}
          setValue={setValue as unknown as UseFormSetValue<Flight>}
          resetField={resetField as unknown as UseFormResetField<Flight>}
          setFocus={setFocus as unknown as UseFormSetFocus<Flight>}
          aircraft={flight.aircraft}
          flightDepartureDateTime={flight.departureDateTime}
          route={routePart}
          passengersLegValue={flight.passengersLeg}
        />
      );
    } else if (infoField.type === "crew") {
      return (
        <EntityCrewPicker
          control={control}
          displayInfoInput={infoField}
          setModalContent={setModalContent}
        />
      );
    } else if (infoField.type === "dropdown") {
      return (
        <EntityDropdown
          displayInfoInput={infoField}
          control={control}
        />
      );
    } else if (infoField.type === "dropdownInput") {
      return (
        <EntityDropdownWithInput
          displayInfoInput={infoField}
          control={control}
        />
      );
    } else if (infoField.type === "passengers") {
      return (
        <EntityPassengersComponent
          displayInfoInput={infoField}
          setModalContent={setModalContent}
          setShowCloseInModalContent={setShowCloseInModalContent}
          control={control}
        />
      );
    } else if (infoField.type === "aftnAddresses"){
      return (
        <EntityAftnAddressesComponent
          displayInfoInput={infoField}
          control={control}
          flightPlanDetails={
            displayInfo.entityName === "Flight Plan"
              ? {
                  setErrorMessage: setErrorMessage,
                  flightPlan: edittedEntity as any as FlightPlan,
                  trigger: trigger,
                }
              : undefined
          }
          flightDetails={
            displayInfo.entityName === "Flight" && (edittedEntity as any as Flight).customRoute
              ? {
                  setErrorMessage: setErrorMessage,
                  flight: edittedEntity as any as Flight,
                  trigger: trigger,
                }
              : undefined
          }
        />
      );
    }else if (infoField.type === "fuelOnBoard"){
      return (
        <EntityFuelOnBoardComponent
          displayInfoInput={infoField}
          control={control}
          edittedFlight={edittedEntity as any as Flight}
        />
      );
    }else if (infoField.type === "flightId"){
      return (
        <EntityFlightIdComponent
          displayInfoInput={infoField}
          control={control}
          edittedFlight={edittedEntity as any as Flight}
        />
      )
    } else if (infoField.type === "pob"){
      return (
        <EntityPobComponent
          displayInfoInput={infoField}
          control={control}
          edittedFlight={edittedEntity as any as Flight}
        />
      )
    } else if (infoField.type === "signature"){
      return (
        <EntitySignatureComponent
          displayInfoInput={infoField}
          control={control}
          entityId={edittedEntity.id}
        />
      )
    } else if (infoField.type === "flightPlanPoint"){
      return (
        <EntityFlightPlanPointPicker
          displayInfoInput={infoField}
          control={control}
          setModalContent={setModalContent}
          setValue={ (displayInfo.entityName === "Flight") ? (setValue as unknown as UseFormSetValue<Flight>) : undefined}
          passengersLegValue={ (displayInfo.entityName === "Flight") ? (edittedEntity as unknown as Flight).passengersLeg : undefined}
        />
      )
    } else if (infoField.type === "customRoute"){
      var flightPlanDeparture: FlightPlanPoint | undefined;
      var flightPlanDestination: FlightPlanPoint | undefined;
      if (displayInfo.entityName === "Flight Plan") {
        flightPlanDeparture = (edittedEntity as unknown as FlightPlan).departure;
        flightPlanDestination = (edittedEntity as unknown as FlightPlan).destination;
      } else if (displayInfo.entityName === "Flight") {
        flightPlanDeparture = (edittedEntity as unknown as Flight).customRoute?.departure;
        flightPlanDestination = (edittedEntity as unknown as Flight).customRoute?.destination;
      } else if (displayInfo.entityName === "ToolsFlight") {
        flightPlanDeparture = (edittedEntity as unknown as ToolsFlight).departure
        flightPlanDestination = (edittedEntity as unknown as ToolsFlight).destination;
      } else {
        throw new Error("Unsupported entity name for EntityCustomRouteComponent");
      }
      
      return (
        <EntityCustomRouteComponent
          displayInfoInput={infoField}
          control={control}
          allowNewCustomPoints={displayInfo.entityName === "Flight Plan"}
          flightPlanDeparture={flightPlanDeparture}
          flightPlanDestination={flightPlanDestination}
          intermediateStops={(displayInfo.entityName === "Flight") ? ((edittedEntity as unknown as Flight).customRoute?.intermediateStops ?? []) : undefined}
          setValue={ (displayInfo.entityName === "Flight") ? (setValue as unknown as UseFormSetValue<Flight>) : undefined}
        />
      )
    } else if (infoField.type === "wb"){
      return (
        <EntityFlightWbComponent
          displayInfoInput={infoField}
          flight={edittedEntity as any as Flight}
          clearWbCalculation={() => (setValue as unknown as UseFormSetValue<Flight>)("wb", { status: "not-calculated", loads: [] })}
          skipWbCalculation={() => (setValue as unknown as UseFormSetValue<Flight>)("wb", { status: "no-airctaft-data", loads: [] })}
          handleSaveFlight={async (flight: Flight) => {
            return new Promise((resolve, reject) => {
              handleSubmit(async (data: any) => {
                dispatchSaveEntity(data).then((savedFlight) => {
                  resolve(savedFlight as unknown as Flight);
                }).catch((err) => {
                  reject(err);
                })
              },(err: any) => {
                reject(err);
              })();
            });
          }}
        />
      );
    } else if (infoField.type === "airfieldSlot"){
      return (
        <EntityFlightAirfieldSlotComponent
          displayInfoInput={infoField}
          control={control}
        />
      )
    } else if (infoField.type === "ifrRoutePlanner"){
      if (displayInfo.entityName !== "Flight" && displayInfo.entityName !== "Flight Plan") {
        throw new Error("Unsupported entity name for EntityIfrRoutePlanner");
      }
      //IFR route planning is only relevant for non-VFR plans.
      const flightRules = (edittedEntity as unknown as Flight | FlightPlan).flightRules
      if (!flightRules || !isIfrFlightRules(flightRules)) {
        return undefined;
      }
      return (
        <EntityIfrRoutePlanner
          displayInfoInput={infoField}
          entityType={displayInfo.entityName}
          edittedEntity={edittedEntity}
          setValue={setValue}
        />
      )
    }
  }

  const canDiscardChanges = Object.keys(dirtyFields).length > 0 || displayInfo.entityName === "ImportedRoute"

  const showDiscardChangesOrClose = () => {
    if (canDiscardChanges){
      setSideAlertContent(
        <Container>
          <Row>
            <Col>
              <h2 className="text-danger">Discard changes?</h2>
            </Col>
          </Row>
          <Row>
            <Col>
              <p>
                Are you sure you want to discard your changes? Any unsaved
                changes will be lost.
              </p>
            </Col>
          </Row>
          <Row className="pt-4 justify-content-center gy-2">
            <Col xs={12}>
              <div className="d-grid g-2">
                <Button
                  onClick={handleClose}
                  variant="danger"
                >
                  Discard Changes
                </Button>
              </div>
            </Col>
            <Col xs={12}>
              <div className="d-grid g-2">
                <Button
                  variant="light"
                  onClick={() => setSideAlertContent(null)}
                >
                  Go Back
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      );

    }else{
      handleClose()
    }
  }


  const discardFlightBriefing = async () => {
    setIsDiscardingFlightBriefing(true)

    await dispatch(discardBriefing(edittedEntity as any as Flight) ).unwrap()
    
    window.location.reload() //TODO: Fix problem with status of flight if not reloaded. 
  }

  const editLockedFlightOffCanvasElement = (
    <Offcanvas backdrop="static" show={showDiscardFlightOffCanvas} placement={"end"}>
      <Offcanvas.Body>
        <Container>
          <Row>
            <Col>
              {lockedFlightReason === "fplFile" && <h2 className="text-danger">Edit Locked Flight?</h2>}
              {lockedFlightReason === "briefing" && <h2 className="text-danger">Delete Briefing Pack?</h2>}
              {lockedFlightReason === "scheduled" && <h2 className="text-danger">Edit Scheduled Flight?</h2>}
            </Col>
          </Row>
          <Row>
            <Col>
              {lockedFlightReason === "fplFile" && <p>The FPL plan has already been filed. Please cancel the FPL first.</p>}
              {lockedFlightReason === "briefing" && (
                <p>Flight Briefing Pack has already been generated for this flight. Are you sure you want to delete them?</p>
              )}
              {lockedFlightReason === "scheduled" && <p>The flight has scheduled actions for FPL File and/or Briefing Pack creation. Please cancel the scheduled actions first.</p>}
            </Col>
          </Row>
          <Row className="pt-4 justify-content-center gy-2">
            <Col xs={12}>
              <div className="d-grid g-2">
                {lockedFlightReason === "fplFile" && (
                  <Button onClick={() => navigate("./fpl")} variant="danger">
                    Cancel FPL
                  </Button>
                )}
                {lockedFlightReason === "briefing" && (
                  <Button disabled={isDiscardingFlightBriefing} onClick={discardFlightBriefing} variant="danger">
                    Delete Briefing pack
                  </Button>
                )}
                {lockedFlightReason === "scheduled" && (
                  <Button onClick={() => navigate("./schedule")} variant="danger">
                    Cancel Scheduled Actions
                  </Button>
                )}
              </div>
            </Col>
            <Col xs={12}>
              <div className="d-grid g-2">
                <Button disabled={isDiscardingFlightBriefing} variant="light" onClick={() => setShowDiscardFlightOffCanvas(false)}>
                  Go Back
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </Offcanvas.Body>
    </Offcanvas>
  );

  const bottomButtons = (
    <Col className="bg-white shadow-lg">
      <Row className="h-100 align-items-center justify-content-center">
        {!lockedFlightReason && (
          <DefaultBottomButtons
            canDiscardChanges={canDiscardChanges}
            isSubmitting={isSubmitting}
            showDiscardChangesOrClose={showDiscardChangesOrClose}
            overrideButtonTitle={displayInfo.nextButtonTitle}
          />
        )}
        {lockedFlightReason && (
          <LockedFlightBottomButtons
            handleClose={handleClose}
            requestEditLockedFlight={() => setShowDiscardFlightOffCanvas(true)}
          />
        )}
      </Row>
    </Col>
  );


  useEffect(() => {
    var newLockedFlightReason: "briefing" | "fplFile" | "scheduled" | undefined = undefined
    if (Object.keys(entity).indexOf("briefing") !== -1 ){
      const flight = entity as any as Flight
      if (flight.briefing) {
        if (flight.briefing.status === "scheduled") { 
          newLockedFlightReason = "scheduled";
        } else if (flight.briefing.status !== "not-started" && flight.briefing.status !== "error") {
          newLockedFlightReason = "briefing";
        }
      }
    }
    if (!newLockedFlightReason && Object.keys(entity).indexOf("fplFile") !== -1 ){
      const fplFile: FlightFplFile = (entity as any).fplFile

      if (fplFile.status === "filed" || fplFile.status === "delayed") {
        newLockedFlightReason = "fplFile"
      }else if (fplFile.status === "scheduled"){
        newLockedFlightReason = "scheduled"
      }
    }
    setLockedFlightReason(newLockedFlightReason)
  }, [entity])

  return (
    <>
      <Modal show size="xl" scrollable contentClassName="h-100" fullscreen="lg-down">
        <Modal.Body className={"p-0"}>
          <ToastContainer position="top-center">
            <Toast autohide delay={5000} onClose={() => setErrorMessage(undefined)} bg="danger" show={(errorMessage?.length ?? 0) > 0}>
              <Toast.Body className="text-white">{errorMessage}</Toast.Body>
            </Toast>
          </ToastContainer>
          <Container fluid className="h-sm-100">
            <Row className="h-100">
              <Col xs={12} sm={4} className="h-sm-100 sticky-top ">
                <Row className="justify-content-between align-items-center pt-4 pb-3 pb-sm-5 bg-sm-down-white shadow-sm-down-sm">
                  <Col xs={"auto"}>
                    <h2 className="my-0">
                      {displayInfo.hardcodedTitle
                        ? displayInfo.hardcodedTitle
                        : (edittedEntity.id ? "Edit " : "Add ") +
                          displayInfo.entityName}
                    </h2>
                  </Col>
                  {extraHeaderContent && edittedEntity.id && (
                    <Col xs={"auto"}>
                      {extraHeaderContent}
                    </Col>
                  )}
                  {dispatchDeleteEntity && edittedEntity.id && (
                    <Col xs={"auto"}>
                      <Button
                        variant="white"
                        className="p-0 me-2"
                        disabled={isSubmitting}
                        onClick={() =>
                         setSideAlertContent( 
                         <Container>
                          <Row>
                            <Col>
                              <h2 className="text-danger"> {(displayInfo.entityCanBeDeleted ?? false) ? "Delete" : "Archive"} {displayInfo.entityName}?</h2>
                            </Col>
                          </Row>
                          <Row>
                            <Col>
                            {(displayInfo.entityCanBeDeleted ?? false) ? (
                                <p>
                                  <span>Deleted {displayInfo.entityName} cannot be recovered.</span>
                                </p>
                            ) : (
                              <>
                                <p>
                                  <span>Archived data will not be deleted from already created Flights.</span>
                                </p>
                                <p>
                                  <span>Are you sure you want to archive this {displayInfo.entityName}?</span>
                                </p>
                              </>
                              )}
                            </Col>
                          </Row>
                          <Row className="py-4 justify-content-center gy-2">
                            <Col xs={12}>
                              <div className="d-grid g-2">
                                <Button
                                  onClick={() => {setSideAlertContent(null); handleDelete()}}
                                  variant="danger"
                                >
                                  {(displayInfo.entityCanBeDeleted ?? false) ? "DELETE" : "ARCHIVE"} 
                                </Button>
                              </div>
                            </Col>
                            <Col xs={12}>
                              <div className="d-grid g-2">
                                <Button
                                  variant="light"
                                  onClick={() => setSideAlertContent(null)}
                                >
                                  Go Back
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        </Container>
                        )}
                      >
                        {(displayInfo.entityCanBeDeleted ?? false) ? (
                          <>
                            <FontAwesomeIcon icon={faTrash} className="me-1"/>Delete
                          </>
                          ) : (
                          <>
                            <FontAwesomeIcon icon={faBoxArchive} className="me-1"/>Archive
                          </>
                        )}
                        
                      </Button>
                    </Col>
                  )}
                </Row>
                <Row className="d-none d-sm-block">
                  <Col>
                    <ul className="nav nav-pills flex-column">
                      {shouldDisplaySections
                        .map((section) => {
                          return (
                            <li
                              key={section.sectionId}
                              className={
                                activeInfoSection.sectionId === section.sectionId
                                  ? "nav-link active"
                                  : "nav-link"
                              }
                              onClick={() => handleClickActiveSection(section)}
                              style={{ cursor: "pointer" }}
                            >
                              {section.sectionName}
                            </li>
                          );
                        })}
                    </ul>
                  </Col>
                </Row>
              </Col>

              <Col xs={12} sm={8} className="bg-white shadow-lg overflow-hidden h-100">
                <Row
                  id="scrollableFormRow"
                  className="overflow-scroll justify-content-center"
                  style={{ height: "calc(100% - 70px)" }}
                >
                  <Col xs={12} xl={10}>
                    <div className="d-none d-sm-block" style={{ height: "40px" }}></div>

                    <Form onSubmit={handleSubmit(handleSubmitAfterValidation)} id={"entityDetailsForm"}>
                      {shouldDisplaySections
                        .map((section) => {
                          //Collapsed sections fold behind their heading until clicked. Their inputs stay
                          //MOUNTED (hidden, not unmounted) so registered form values and validation are
                          //unaffected by whether the section happens to be open.
                          const isCollapsed = section.collapsedByDefault === true && !expandedSections[section.sectionId];
                          return (
                            <div
                              id={section.sectionId}
                              key={section.sectionId}
                              className="py-2 scrollableFormRowSection"
                            >
                              {section.collapsedByDefault ? (
                                <p
                                  className="h3 py-2 d-flex align-items-center justify-content-between gap-2"
                                  style={{ cursor: "pointer" }}
                                  onClick={() =>
                                    setExpandedSections((prev) => ({ ...prev, [section.sectionId]: !prev[section.sectionId] }))
                                  }
                                >
                                  {section.sectionName}
                                  <FontAwesomeIcon
                                    icon={faCaretRight}
                                    className="text-muted"
                                    style={{ transition: "transform 0.15s ease", transform: isCollapsed ? "none" : "rotate(90deg)" }}
                                  />
                                </p>
                              ) : (
                                <p className="h3 py-2">{section.sectionName}</p>
                              )}
                              <div style={isCollapsed ? { display: "none" } : undefined}>
                              {section.inputs.map((i) => {
                                const inputField = getSpecificInfoField(i);
                                if (!inputField) {
                                  return null;
                                }
                                return (
                                  <div className="mb-3" key={i.key as string}>
                                    {inputField}
                                  </div>
                                );
                              })}
                              </div>
                            </div>
                          );
                        })}
                    </Form>
                    <div style={{ height: "75vh" }}></div>
                  </Col>
                </Row>

                <Row className="d-none d-sm-flex" style={{ height: "70px", borderCollapse: "separate" }}>
                  {bottomButtons}
                </Row>

                <Container className="d-sm-none fixed-bottom">
                  <Row style={{ height: "70px", borderCollapse: "separate" }}>{bottomButtons}</Row>
                </Container>

              </Col>
            </Row>
          </Container>
        </Modal.Body>
      </Modal>
      <Offcanvas
        backdrop="static"
        show={modalContent}
        placement={"end"}
        className="offcanvas-size-fullscreen-xl"
      >
        {showCloseInModalContent && (
          <Button variant="close" className="position-absolute top-0 end-0 p-4" onClick={() => setModalContent(null)}></Button>
        )}
        <Offcanvas.Body className="p-0">{modalContent}</Offcanvas.Body>
      </Offcanvas>

      <Offcanvas
        backdrop="static"
        show={sideAlertContent}
        placement={ "end" }
        className="offcanvas-size-md"
      >
        <Offcanvas.Body>{sideAlertContent}</Offcanvas.Body>
      </Offcanvas>
      
      {editLockedFlightOffCanvasElement}

      <Outlet />
    </>
  );
}

export default EntityDetailsPage

interface DefaultBottomButtonsProps {
  isSubmitting: boolean;
  canDiscardChanges: boolean;
  showDiscardChangesOrClose: () => void;
  overrideButtonTitle?: string;
}

function DefaultBottomButtons({isSubmitting, canDiscardChanges, showDiscardChangesOrClose, overrideButtonTitle}: DefaultBottomButtonsProps) {
  return (
    <>
      <Col xs={4}>
        <div className="d-grid g-2">
          <Button
            disabled={isSubmitting}
            onClick={showDiscardChangesOrClose}
            variant="light"
          >
            {canDiscardChanges ? "Discard Changes" : "Close"}
          </Button>
        </div>
      </Col>
      <Col xs={8} lg={4}>
        <div className="d-grid g-2">
          <Button
            disabled={isSubmitting}
            form="entityDetailsForm"
            type="submit"
            variant="dark"
          >
            {overrideButtonTitle ? overrideButtonTitle : "Save"}
          </Button>
        </div>
      </Col>
    </>
  );
}

interface LockedFlightBottomButtonsProps {
  handleClose: () => void;
  requestEditLockedFlight: () => void;
}

function LockedFlightBottomButtons({ handleClose, requestEditLockedFlight}: LockedFlightBottomButtonsProps) {
  return (
    <>
      <Col xs={4}>
        <div className="d-grid g-2">
          <Button
            onClick={requestEditLockedFlight}
            variant="light"
          >
            Edit Flight
          </Button>
        </div>
      </Col>
      <Col xs={8} lg={4}>
        <div className="d-grid g-2">
          <Button
            onClick={handleClose}
            variant="dark"
          >
            Close
          </Button>
        </div>
      </Col>
    </>
  );
}